import { ApiService } from '@services/ApiService'
import { makeAutoObservable, runInAction } from 'mobx'

// Types only: the store creates the sessions that create these lists.
import type { AssistantConversationSummary } from './assistantStore'

/** The placement and the record a placement's History lists the conversations of. */
export interface AssistantPlacementHistoryScope {
  placement: string
  subjectType: string
  subjectId: string
}

/**
 * Where the list learns the titles the stream brought (see AssistantStore.applyTitle): a page
 * that was asked for before a title arrived still carries the old one.
 */
export interface AssistantTitleSource {
  titleMark: () => number
  withStreamedTitles: (
    _items: AssistantConversationSummary[],
    _seq: number
  ) => AssistantConversationSummary[]
}

const isOlder = (a: string, b: string) => Date.parse(a) < Date.parse(b)

/**
 * The History of a placement whose list is its own (historyScope 'placement', e.g. the Case
 * history pane): only the user's conversations of that placement about its record, newest first,
 * a page at a time. Kept by the placement's session, apart from the drawer's list: its filter,
 * its pages and its rows are never touched by this one. Nothing is read before the History is
 * opened; after a turn it is read again only while it is open (see
 * AssistantStore.refreshConversationsAfterTurn and refreshIfRequested).
 */
export class AssistantPlacementHistory {
  /** The pages loaded so far, newest first. */
  items: AssistantConversationSummary[] = []
  /** How many conversations the server holds for the scope, loaded or not. */
  total = 0
  /** Older conversations than the loaded ones exist (see loadMore). */
  hasMore = false
  /** The server has answered at least once (or there is no record to ask about). */
  loaded = false
  /** The first page is being read (again). */
  loading = false
  loadingMore = false
  /** The last read of the first page failed; the rows on screen (if any) are from before. */
  failed = false

  private readonly scope: () => AssistantPlacementHistoryScope | null
  private readonly titles: AssistantTitleSource
  /** The list has been asked for at least once (see refreshIfRequested). */
  private requested = false
  /** Identifies the newest request; an older one that answers late is ignored. */
  private request = 0

  constructor(
    scope: () => AssistantPlacementHistoryScope | null,
    titles: AssistantTitleSource
  ) {
    this.scope = scope
    this.titles = titles
    makeAutoObservable<
      AssistantPlacementHistory,
      'scope' | 'titles' | 'requested' | 'request'
    >(this, {
      scope: false,
      titles: false,
      requested: false,
      request: false,
    })
  }

  /**
   * Reads the first page (again). Older pages loaded with "Load more" stay: the first page is
   * what changes when a turn ends (a new conversation, a new title, another order). Without a
   * record the server could take, there is nothing to list and nothing is asked.
   */
  load = async () => {
    this.requested = true
    const scope = this.scope()
    const request = ++this.request
    if (!scope) {
      this.items = []
      this.total = 0
      this.hasMore = false
      this.loaded = true
      this.loading = false
      this.loadingMore = false
      this.failed = false
      return
    }
    const seq = this.titles.titleMark()
    this.loading = true
    try {
      const res = await ApiService.Assistant.GetConversations({ ...scope })
      runInAction(() => {
        if (request !== this.request) {
          return
        }
        const page = this.titles.withStreamedTitles(res.data?.items ?? [], seq)
        const hasMore = res.data?.hasMore === true
        const oldest = page[page.length - 1]
        const kept =
          hasMore && oldest
            ? this.items.filter(
                c =>
                  !page.some(item => item.id === c.id) &&
                  isOlder(c.lastMessageAt, oldest.lastMessageAt)
              )
            : []
        this.items = [...page, ...kept]
        this.hasMore = kept.length ? this.hasMore : hasMore
        this.total = Math.max(
          typeof res.data?.totalCount === 'number' ? res.data.totalCount : 0,
          this.items.length
        )
        this.loaded = true
        this.loading = false
        this.failed = false
      })
    } catch {
      runInAction(() => {
        if (request !== this.request) {
          return
        }
        this.loading = false
        this.failed = true
      })
    }
  }

  /**
   * A turn of the placement's session ended while its History is open: the list is read again,
   * but only once it was asked for on this page (the pane has no list on screen until its History
   * is opened).
   */
  refreshIfRequested = () => {
    if (this.requested) {
      this.load()
    }
  }

  /** The next page: the conversations older than the ones loaded so far. */
  loadMore = async () => {
    const scope = this.scope()
    const last = this.items[this.items.length - 1]
    if (!scope || !last || !this.hasMore || this.loadingMore || this.loading) {
      return
    }
    const seq = this.titles.titleMark()
    const request = ++this.request
    this.loadingMore = true
    try {
      const res = await ApiService.Assistant.GetConversations({
        ...scope,
        beforeLastMessageAt: last.lastMessageAt,
      })
      runInAction(() => {
        if (request !== this.request) {
          return
        }
        const known = new Set(this.items.map(c => c.id))
        const page = this.titles.withStreamedTitles(res.data?.items ?? [], seq)
        this.items = [
          ...this.items,
          ...page.filter(item => !known.has(item.id)),
        ]
        this.hasMore = res.data?.hasMore === true
        this.total = Math.max(
          typeof res.data?.totalCount === 'number' ? res.data.totalCount : 0,
          this.items.length
        )
      })
    } catch {
      // "Load more" stays where it is and can be pressed again
    } finally {
      runInAction(() => {
        this.loadingMore = false
      })
    }
  }

  /** A conversation was given a title (the stream, a rename here or in the drawer). */
  applyTitle = (id: string, title: string) => {
    const summary = this.items.find(c => c.id === id)
    if (summary) {
      summary.title = title
    }
  }

  /** A conversation was deleted (here or in the drawer). */
  remove = (id: string) => {
    if (this.items.some(c => c.id === id)) {
      this.total = Math.max(0, this.total - 1)
      this.items = this.items.filter(c => c.id !== id)
    }
  }

  /** The session is dropped: an answer still on its way lands nowhere. */
  dispose = () => {
    this.request++
    this.loading = false
    this.loadingMore = false
  }
}
